import { useEffect, useRef, useState } from 'react'
import { Icon, useAssetsContext, Tenant } from 'summon-ui'
import { useTranslation } from 'summon-ui/intl'
import { BackgroundImage, Button, Divider, Group, Paper, Stack, Tabs, Text, Title } from 'summon-ui/mantine'
import { TENANT_CONFIG } from '@config'
import { G7LevelUpData, LOLevelUpData } from '@features/home/components/levelUp/data'
import useClaim from '@features/home/hooks/useClaim'
import useMintLevelUp from '@features/home/hooks/useMintLevelUp'
import { ClaimableItemType } from '@generated/generates'

// TODO: We need a scalable solution for levels info
const { name } = TENANT_CONFIG
const isG7 = name === Tenant.G7

enum OnchainLevelTabsOptions {
  Chronicles = 'Chronicles',
  Rewards = 'Rewards'
}

type LevelUpModalProps = {
  open: boolean
  close: () => void
  currentLevel: number
}

const OnChainLevelUpModal = ({ currentLevel, close }: LevelUpModalProps) => {
  const t = useTranslation()
  const { getTenantImage } = useAssetsContext()
  const levelCardImage = getTenantImage(`level-${currentLevel + 1}-card`)
  const { claimableItems } = useClaim(ClaimableItemType.Level)
  const [step, setStep] = useState(0)
  const { mint, isMinting, disabled: isMintDisabled } = useMintLevelUp()
  const claimable = claimableItems?.find(
    (claimable) => claimable.isUsed === false && claimable.value === currentLevel + 1
  )
  const levelData = isG7 ? G7LevelUpData(Number(currentLevel)) : LOLevelUpData(Number(currentLevel))

  const audio = useRef<HTMLAudioElement>()
  // start the audio (using the .current property of the ref we just created) when the component mounts using the useEffect hook
  useEffect(() => {
    audio.current = new Audio(levelData?.audio)
    audio.current.volume = 0.2
    return () => {
      audio.current?.pause()
    }
  }, [])

  const closeLevelModal = () => {
    setStep(0)
    close()
  }

  const claim = async () => {
    await mint(
      {},
      {
        onSettled: closeLevelModal
      }
    )
  }

  const TabsList = () => (
    <Tabs.List grow mb='md' bg='gray.8'>
      {Object.values(OnchainLevelTabsOptions).map((tab) => (
        <Tabs.Tab key={tab} value={tab}>
          {tab}
        </Tabs.Tab>
      ))}
    </Tabs.List>
  )

  return (
    <>
      <Stack p='md' gap='sm'>
        <div>
          <Title order={3}>{levelData?.header}</Title>
          <Text>{levelData?.subheader}</Text>
        </div>

        <Tabs defaultValue={OnchainLevelTabsOptions.Chronicles} keepMounted={false} mt='md'>
          <TabsList />
          <Tabs.Panel value={OnchainLevelTabsOptions.Chronicles}>
            <BackgroundImage
              src={levelCardImage as string}
              radius='md'
              h={310}
              onClick={() => (audio.current?.paused ? audio.current?.play() : audio.current?.pause())}
              className='cursor-pointer'
            >
              {audio.current?.paused && <Icon name='PlayCircle' variant='light' size='xl' c='white' />}
            </BackgroundImage>

            <Title order={4} size='sm' mt='md'>
              {levelData?.description}
            </Title>
          </Tabs.Panel>
          <Tabs.Panel value={OnchainLevelTabsOptions.Rewards}>
            <BackgroundImage
              src={levelData?.levelCollection as string}
              radius='md'
              h={310}
              onClick={() => (audio.current?.paused ? audio.current?.play() : audio.current?.pause())}
            />
            <Title order={4} my='xs'>
              {t('Items Earned')}{' '}
            </Title>
            <Group gap='xs'>
              {[...Array(claimable?.itemCount).keys()].map((item) => (
                <Paper key={item} withBorder radius='md' maw={150} p='xs'>
                  <img className='h-full opacity-50' src={levelData?.levelCollectionItems[item]} alt='Mystery Box' />
                  <Text size='xs' fw={500} mt='xs' ta='center'>
                    {levelData?.levelCollectionName}
                  </Text>
                </Paper>
              ))}
            </Group>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <Divider />

      <Group justify='space-between' w='100%' p='sm'>
        <Button variant='outline' miw={100} onClick={closeLevelModal}>
          {t('Cancel')}
        </Button>
        {step ? (
          <Group>
            <Group gap='sm' wrap='nowrap'>
              <Button miw={100} disabled={isMintDisabled} onClick={claim} loading={isMinting}>
                {t('Claim')}
              </Button>
            </Group>
          </Group>
        ) : (
          <Button
            miw={100}
            onClick={() => {
              setStep(1)
            }}
          >
            {t('Proceed')}
          </Button>
        )}
      </Group>
    </>
  )
}

export default OnChainLevelUpModal
